import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type { ArchetypeWithSubarchetypes } from "@liga/shared";
import { api } from "../api/client";
import { ErrorBox, Loading } from "./Loading";

type SubInPanel = ArchetypeWithSubarchetypes["subarchetypes"][number];

/**
 * Master list editor for the deck classification taxonomy:
 *   Archetype (e.g. "Aggro") -> Subarchetype (e.g. "Burn/Red Prowess").
 *
 * The same taxonomy populates the dropdowns in the Decks admin and the
 * Melee import flow, so any new Subarchetype added here becomes
 * immediately selectable everywhere.
 */
export function ArchetypesAdminPanel() {
  const queryClient = useQueryClient();
  const query = useQuery<ArchetypeWithSubarchetypes[]>({
    queryKey: ["admin", "archetypes"],
    queryFn: api.adminArchetypes,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "archetypes"] });
    queryClient.invalidateQueries({ queryKey: ["archetypes"] });
    queryClient.invalidateQueries({ queryKey: ["admin", "subarchetypes"] });
    queryClient.invalidateQueries({ queryKey: ["admin", "decks"] });
    queryClient.invalidateQueries({ queryKey: ["decks"] });
    // The Metagame grid joins each row's banner from the card_images cache
    // via `representative_card_name`, so any change here can affect the
    // public view even before the user navigates away.
    queryClient.invalidateQueries({ queryKey: ["metagame"] });
  };

  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [newArchetypeName, setNewArchetypeName] = useState("");
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [newSubByArchetype, setNewSubByArchetype] = useState<
    Record<number, string>
  >({});

  const createArchetype = useMutation({
    mutationFn: api.adminCreateArchetype,
    onSuccess: () => {
      invalidate();
      setNewArchetypeName("");
      setErrorMsg(null);
    },
    onError: (err: Error) => setErrorMsg(err.message),
  });

  const renameArchetype = useMutation({
    mutationFn: (input: { id: number; name: string }) =>
      api.adminRenameArchetype(input.id, { name: input.name }),
    onSuccess: () => {
      invalidate();
      setRenamingId(null);
      setRenameDraft("");
      setErrorMsg(null);
    },
    onError: (err: Error) => setErrorMsg(err.message),
  });

  const createSubarchetype = useMutation({
    mutationFn: api.adminCreateSubarchetype,
    onSuccess: (_, vars) => {
      invalidate();
      setNewSubByArchetype((prev) => ({ ...prev, [vars.archetypeId]: "" }));
      setErrorMsg(null);
    },
    onError: (err: Error) => setErrorMsg(err.message),
  });

  const moveSubarchetype = useMutation({
    mutationFn: (input: { id: number; archetypeId: number }) =>
      api.adminUpdateSubarchetype(input.id, { archetypeId: input.archetypeId }),
    onSuccess: () => {
      invalidate();
      setErrorMsg(null);
    },
    onError: (err: Error) => setErrorMsg(err.message),
  });

  const setRepresentativeCard = useMutation({
    mutationFn: (input: { id: number; cardName: string | null }) =>
      api.adminSetSubarchetypeRepresentativeCard(input.id, {
        cardName: input.cardName,
      }),
    onSuccess: () => {
      invalidate();
      setErrorMsg(null);
    },
    onError: (err: Error) => setErrorMsg(err.message),
  });

  const archetypes = useMemo(() => query.data ?? [], [query.data]);

  return (
    <section className="rounded-2xl border border-line bg-white p-6 space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#8a8170] font-bold mb-1.5">
            Taxonomia
          </div>
          <h2 className="font-display text-xl font-bold text-ink">
            Arquétipos &amp; Subarquétipos
          </h2>
          <p className="text-[14px] leading-relaxed text-[#6b6356] mt-1">
            Cadastre os arquétipos (categorias amplas como "Aggro", "Tempo") e
            os subarquétipos que pertencem a cada um. Eles são usados para
            classificar cada deck no momento da importação.
          </p>
        </div>
      </header>

      {errorMsg && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm flex items-start gap-3">
          <span className="flex-1">{errorMsg}</span>
          <button
            type="button"
            onClick={() => setErrorMsg(null)}
            className="text-red-500 hover:text-red-700 font-bold"
            aria-label="Fechar aviso"
          >
            ×
          </button>
        </div>
      )}

      <form
        className="flex items-center gap-2 flex-wrap"
        onSubmit={(e) => {
          e.preventDefault();
          if (!newArchetypeName.trim()) return;
          createArchetype.mutate({ name: newArchetypeName.trim() });
        }}
      >
        <input
          type="text"
          value={newArchetypeName}
          onChange={(e) => setNewArchetypeName(e.target.value)}
          placeholder="Novo arquétipo (ex.: Tempo)"
          className="w-72 max-w-full rounded border border-slate-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        <button
          type="submit"
          disabled={createArchetype.isPending || !newArchetypeName.trim()}
          className="rounded-lg bg-brand-700 hover:bg-brand-800 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 text-sm font-semibold transition-colors"
        >
          Adicionar arquétipo
        </button>
      </form>

      {query.isLoading ? (
        <Loading label="Carregando arquétipos..." />
      ) : query.error ? (
        <ErrorBox error={query.error} />
      ) : archetypes.length === 0 ? (
        <p className="text-sm text-[#8a8170] italic">
          Nenhum arquétipo cadastrado ainda.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {archetypes.map((arch) => {
            const subDraft = newSubByArchetype[arch.id] ?? "";
            return (
              <article
                key={arch.id}
                className="rounded-xl border border-brand-100 bg-brand-50/30 p-4 space-y-3"
              >
                <header className="flex items-center justify-between gap-2">
                  {renamingId === arch.id ? (
                    <form
                      className="flex-1 flex items-center gap-2"
                      onSubmit={(e) => {
                        e.preventDefault();
                        renameArchetype.mutate({
                          id: arch.id,
                          name: renameDraft.trim() || arch.name,
                        });
                      }}
                    >
                      <input
                        autoFocus
                        type="text"
                        value={renameDraft}
                        onChange={(e) => setRenameDraft(e.target.value)}
                        className="flex-1 rounded border border-slate-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                      />
                      <button
                        type="submit"
                        className="text-sm font-semibold text-brand-700 hover:underline"
                      >
                        Salvar
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setRenamingId(null);
                          setRenameDraft("");
                        }}
                        className="text-sm text-[#6b6356] hover:text-ink"
                      >
                        Cancelar
                      </button>
                    </form>
                  ) : (
                    <>
                      <h3 className="font-display text-lg font-bold text-ink">
                        {arch.name}
                      </h3>
                      <button
                        type="button"
                        onClick={() => {
                          setRenamingId(arch.id);
                          setRenameDraft(arch.name);
                        }}
                        className="text-xs font-semibold text-brand-700 hover:underline"
                      >
                        Renomear
                      </button>
                    </>
                  )}
                </header>

                {arch.subarchetypes.length === 0 ? (
                  <p className="text-xs text-[#8a8170] italic">
                    Nenhum subarquétipo cadastrado.
                  </p>
                ) : (
                  <ul className="space-y-1.5">
                    {arch.subarchetypes.map((s) => (
                      <li
                        key={s.id}
                        className="flex items-center gap-2.5 bg-white rounded border border-line px-2.5 py-2 text-sm"
                      >
                        <RepresentativeCardThumb sub={s} />
                        <div className="flex-1 min-w-0 flex flex-col gap-1">
                          <span className="text-ink font-medium truncate">
                            {s.name}
                          </span>
                          <RepresentativeCardEditor
                            sub={s}
                            isSaving={
                              setRepresentativeCard.isPending &&
                              setRepresentativeCard.variables?.id === s.id
                            }
                            onSave={(cardName) =>
                              setRepresentativeCard.mutate({
                                id: s.id,
                                cardName,
                              })
                            }
                          />
                        </div>
                        <MoveSubarchetypeSelect
                          subarchetypeId={s.id}
                          currentArchetypeId={arch.id}
                          archetypes={archetypes}
                          onMove={(archetypeId) =>
                            moveSubarchetype.mutate({
                              id: s.id,
                              archetypeId,
                            })
                          }
                        />
                      </li>
                    ))}
                  </ul>
                )}

                <form
                  className="flex items-center gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!subDraft.trim()) return;
                    createSubarchetype.mutate({
                      name: subDraft.trim(),
                      archetypeId: arch.id,
                    });
                  }}
                >
                  <input
                    type="text"
                    value={subDraft}
                    onChange={(e) =>
                      setNewSubByArchetype((prev) => ({
                        ...prev,
                        [arch.id]: e.target.value,
                      }))
                    }
                    placeholder="Novo subarquétipo"
                    className="flex-1 rounded border border-slate-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                  <button
                    type="submit"
                    disabled={createSubarchetype.isPending || !subDraft.trim()}
                    className="rounded-md bg-brand-700 hover:bg-brand-800 disabled:opacity-50 text-white px-3 py-1 text-xs font-semibold transition-colors"
                  >
                    Adicionar
                  </button>
                </form>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

interface MoveProps {
  subarchetypeId: number;
  currentArchetypeId: number;
  archetypes: ArchetypeWithSubarchetypes[];
  onMove: (archetypeId: number) => void;
}

function MoveSubarchetypeSelect({
  currentArchetypeId,
  archetypes,
  onMove,
}: MoveProps) {
  return (
    <select
      aria-label="Mover para outro arquétipo"
      value={currentArchetypeId}
      onChange={(e) => {
        const next = Number(e.target.value);
        if (next !== currentArchetypeId) onMove(next);
      }}
      className="text-xs rounded border border-slate-300 bg-white px-1.5 py-0.5"
    >
      {archetypes.map((a) => (
        <option key={a.id} value={a.id}>
          {a.name}
        </option>
      ))}
    </select>
  );
}

function RepresentativeCardThumb({ sub }: { sub: SubInPanel }) {
  const url = sub.representativeCard?.imageArtCropUrl ?? null;
  return (
    <div className="flex-none w-16 h-9 rounded-md overflow-hidden border border-line bg-[#efece6]">
      {url ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          className="block w-full h-full object-cover"
        />
      ) : (
        <div
          className="w-full h-full bg-[repeating-linear-gradient(45deg,#efece6_0_8px,#f3efe6_8px_16px)]"
          aria-hidden="true"
        />
      )}
    </div>
  );
}

interface RepresentativeCardEditorProps {
  sub: SubInPanel;
  isSaving: boolean;
  onSave: (cardName: string | null) => void;
}

function RepresentativeCardEditor({
  sub,
  isSaving,
  onSave,
}: RepresentativeCardEditorProps) {
  const initial = sub.representativeCardName ?? "";
  const [draft, setDraft] = useState(initial);

  // Keep the input in sync when the server replies with an updated value
  // (or when the row is replaced because we navigated archetypes around).
  useEffect(() => {
    setDraft(initial);
  }, [initial]);

  const dirty = draft.trim() !== initial.trim();

  function commit() {
    const next = draft.trim();
    if (next === initial.trim()) return;
    onSave(next.length === 0 ? null : next);
  }

  return (
    <form
      className="flex items-center gap-1.5"
      onSubmit={(e) => {
        e.preventDefault();
        commit();
      }}
    >
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Carta representativa (ex.: Delver of Secrets)"
        className="flex-1 min-w-0 rounded border border-slate-300 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
      />
      <button
        type="submit"
        disabled={isSaving || !dirty}
        className="text-[11px] font-semibold rounded-md bg-brand-700 hover:bg-brand-800 disabled:opacity-40 disabled:cursor-not-allowed text-white px-2 py-1 transition-colors"
      >
        {isSaving ? "..." : "Salvar"}
      </button>
      {initial && !dirty && (
        <button
          type="button"
          onClick={() => onSave(null)}
          disabled={isSaving}
          className="text-[11px] font-semibold text-[#6b6356] hover:text-ink disabled:opacity-50"
          aria-label="Remover carta representativa"
          title="Remover carta representativa"
        >
          ×
        </button>
      )}
    </form>
  );
}
